import { useQuery } from '@tanstack/react-query';
import { isBefore } from 'date-fns';
import { useCallback, useState } from 'react';
import isEqual from 'react-fast-compare';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { PREVIOUS_DAYS_TO_PULL } from 'src/navigations/Routes/Home/RecentSalesSection/useRecentSalesSection';
import { stringToUtcDate } from 'src/utils/dateTimeUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import { Sale, SaleClient } from 'src/WebApiController';

export const useGetRecentSalesForEvents = (disable?: boolean) => {
  const { activeAccountWebClientConfig } = useAppContext();

  const { trackError } = useClientLogContext();

  const [recentSales, setRecentSales] = useState<Sale[]>([]);
  const [minRowVersion, setMinRowVersion] = useState<number>();

  const getSalesForEvents = useCallback(async () => {
    if (disable || !activeAccountWebClientConfig?.activeAccountId) {
      return null;
    }
    return tryInvokeApi(
      async () => {
        const client = new SaleClient(activeAccountWebClientConfig);
        const r = await client.getRecentSales(
          minRowVersion && minRowVersion + 1
        ); // since the sproc does >=
        if (r.item2 > (minRowVersion ?? 0) + 1) {
          setMinRowVersion(r.item2);
        }

        // First filter out the sales that are older than the last N days
        const today = new Date();
        const lastNDays = new Date();
        lastNDays.setDate(today.getDate() - PREVIOUS_DAYS_TO_PULL);
        let newRecentSales = recentSales.filter(
          (sale) => new Date(sale.saleDate) >= lastNDays
        );

        const salesObjects = newRecentSales.reduce(
          (r, cur) => {
            r[cur.id] = cur;
            return r;
          },
          {} as Record<string, Sale>
        );

        // Merge in the new sales from server
        r.item1.forEach((sale) => {
          salesObjects[sale.id] = sale;
        });

        // Sort the sales by date
        newRecentSales = Object.values(salesObjects).toSorted(
          (a, b) =>
            new Date(a.saleDate).getTime() - new Date(b.saleDate).getTime()
        );

        if (!isEqual(newRecentSales, recentSales)) {
          setRecentSales(newRecentSales);
        }

        return { item1: newRecentSales, item2: r.item2 };
      },
      (error) => {
        trackError('SaleClient.getSalesForEvents.recent', error);
      }
    );
  }, [
    activeAccountWebClientConfig,
    disable,
    minRowVersion,
    recentSales,
    trackError,
  ]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      'SaleClient.getSalesForEvents.recent',
      activeAccountWebClientConfig.activeAccountId,
    ],
    queryFn: getSalesForEvents,
    enabled: !!activeAccountWebClientConfig?.activeAccountId && !disable,
    refetchOnWindowFocus: false,
    networkMode: 'offlineFirst',
    refetchInterval: 30000, // 30 seconds
    refetchIntervalInBackground: true,
  });

  return {
    recentSalesData: data?.item1,
    pastRecentSales: data?.item1?.filter((sale) =>
      isBefore(new Date(stringToUtcDate(sale.saleDate)), new Date())
    ),
    isLoading,
    isFetching,
  };
};
